import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, RotateCw, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { AdminTitle, Spinner } from "@/admin/AdminLayout";
import { Section } from "@/admin/hr/common";

const ST = { sent: ["Gönderildi", "text-emerald-700"], failed: ["Başarısız", "text-red-700"], pending: ["Bekliyor", "text-amber-700"] };

function Provider({ cfg }) {
  const last = cfg.last_status;
  return (
    <Section title="Gönderim servisi" testid="email-provider">
      <p className="text-sm">Aktif servis: <b data-testid="email-provider-name">{cfg.provider}</b></p>
      {!cfg.smtp_configured && <p className="mt-2 text-xs text-zinc-500">Gmail SMTP henüz yapılandırılmadı. Google hesabınızdan 16 haneli uygulama şifresi oluşturup iletirseniz gönderim Gmail SMTP üzerinden yapılır; şifre yalnızca sunucudaki gizli ortam değişkenlerinde saklanır.</p>}
      {last && (
        <p className={`mt-3 flex items-start gap-2 text-sm ${last.ok ? "text-emerald-700" : "text-red-700"}`} data-testid="email-last-status">
          {last.ok ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <AlertTriangle className="h-4 w-4 shrink-0" />}
          Son gönderim {fmtDate(last.at)}: {last.ok ? "başarılı" : `başarısız – ${last.error}`}
        </p>
      )}
    </Section>
  );
}

function LogRow({ l, busy, onRetry }) {
  return (
    <tr data-testid={`email-log-${l.id}`}>
      <td className="whitespace-nowrap py-2 pr-3 text-zinc-500">{fmtDate(l.created_at)}</td>
      <td className="py-2 pr-3">{l.subject}</td>
      <td className="py-2 pr-3">{l.to}</td>
      <td className={`py-2 pr-3 font-semibold ${ST[l.status]?.[1]}`} title={l.error}>{ST[l.status]?.[0]} {l.attempts > 1 && <span className="text-xs font-normal text-zinc-500">({l.attempts} deneme)</span>}{l.error && <span className="block text-xs font-normal text-zinc-500">{l.error}</span>}</td>
      <td className="py-2">{l.status === "failed" && <button onClick={() => onRetry(l.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-[color:var(--red)]" data-testid={`email-retry-${l.id}`}>{busy === l.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />} Yeniden dene</button>}</td>
    </tr>
  );
}

function LogTable({ logs, busy, onRetry }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wider text-zinc-500"><tr><th className="py-2 pr-3">Tarih</th><th className="py-2 pr-3">Konu</th><th className="py-2 pr-3">Alıcı</th><th className="py-2 pr-3">Durum</th><th /></tr></thead>
        <tbody className="divide-y divide-zinc-100">{logs.map((l) => <LogRow key={l.id} l={l} busy={busy} onRetry={onRetry} />)}</tbody>
      </table>
    </div>
  );
}

export default function Notifications() {
  const [cfg, setCfg] = useState(null);
  const [logs, setLogs] = useState([]);
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState("");
  const load = async () => {
    const [c, l] = await Promise.all([api.get("/admin/hr/email/config"), api.get("/admin/hr/email/logs")]);
    setCfg(c.data);
    setEmails(c.data.recipients.join("\n"));
    setLogs(l.data);
  };
  useEffect(() => {
    load().catch((e) => toast.error(errMsg(e)));
  }, []);
  const act = async (key, fn, ok) => {
    setBusy(key);
    try {
      await fn();
      toast.success(ok);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
      load();
    }
  };
  const save = () => act("save", () => api.put("/admin/settings", { notification_emails: emails.split("\n").map((s) => s.trim()).filter(Boolean) }), "Alıcılar kaydedildi");
  const test = () => act("test", () => api.post("/admin/settings/test-email"), "Test e-postası gönderildi; gelen kutunuzu (ve spam klasörünü) kontrol edin");
  if (!cfg) return <Spinner />;
  return (
    <div className="max-w-4xl space-y-4">
      <AdminTitle title="E-posta Bildirim Ayarları" desc="Yeni iş başvurusu ve geri arama taleplerinde otomatik bildirim gönderilir. Kişisel belgeler e-postaya eklenmez." />
      <Provider cfg={cfg} />
      <Section title="Alıcı adresleri" testid="email-recipients">
        <textarea rows={3} value={emails} onChange={(e) => setEmails(e.target.value)} className="field-input" data-testid="email-recipients-input" />
        <p className="mt-1 text-xs text-zinc-500">Her satıra bir adres (en fazla 5).</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={save} className="btn-outline !py-2" data-testid="email-recipients-save">{busy === "save" && <Loader2 className="h-4 w-4 animate-spin" />} Kaydet</button>
          <button onClick={test} className="btn-red !py-2" data-testid="email-test-btn">{busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Test E-postası Gönder</button>
        </div>
      </Section>
      <Section title={`Gönderim kayıtları${cfg.failed ? ` · ${cfg.failed} başarısız` : ""}`} testid="email-logs">
        <p className="mb-3 text-xs text-zinc-500">Başarısız gönderimler 48 saat boyunca her 15 dakikada bir otomatik olarak yeniden denenir; dilerseniz elle de tekrar gönderebilirsiniz.</p>
        {logs.length === 0 ? <p className="text-sm text-zinc-500">Henüz bildirim gönderilmedi.</p> : <LogTable logs={logs} busy={busy} onRetry={(id) => act(id, () => api.post(`/admin/hr/email/logs/${id}/retry`), "Yeniden gönderildi")} />}
      </Section>
    </div>
  );
}
